import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  BarChart3,
  FileText,
  Mic,
  TrendingUp,
  Map,
  LogOut,
  X,
  Building2,
  Calculator,
  Eye
} from 'lucide-react';

interface NavigationProps {
  onClose?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const adminLinks = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/comparison', icon: BarChart3, label: 'Store Comparison' },
    { path: '/admin/users', icon: Users, label: 'User Management' },
  ];

  const managerLinks = [
    { path: '/manager', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/manager/payroll', icon: Calculator, label: 'Payroll Entry' },
    { path: '/manager/voice', icon: Mic, label: 'Voice Assistant' },
  ];

  const franchiseeLinks = [
    { path: '/franchisee', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/franchisee/revenue-3d', icon: Map, label: '3D Revenue' },
    { path: '/franchisee/predictive', icon: TrendingUp, label: 'Predictive Analytics' },
  ];

  const getNavigationLinks = () => {
    switch (user?.role) {
      case 'admin':
        return adminLinks;
      case 'manager':
        return managerLinks;
      case 'franchisee':
        return franchiseeLinks;
      default:
        return [];
    }
  };

  const navigationLinks = getNavigationLinks();

  return (
    <nav className="h-full flex flex-col bg-gradient-to-b from-gray-900 to-gray-800 text-white">
      {/* Logo Section */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">TanOak</h2>
              <p className="text-xs text-gray-400">Platform</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* User Info */}
      <div className="px-6 py-4 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
            <span className="text-lg font-semibold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium">{user?.name || 'User'}</p>
            <p className="text-xs text-gray-400 capitalize">{user?.role || 'guest'}</p>
          </div>
        </div>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {navigationLinks.map((link) => (
            <li key={link.path}>
              <NavLink
                to={link.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`
                }
              >
                <link.icon className="h-5 w-5" />
                <span className="font-medium">{link.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {/* Bottom Section */}
      <div className="p-4 border-t border-gray-700">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200"
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;